import { brandColorTokens } from "@constants/brandColors";
import { brandRadius } from "@constants/brandShape";

import { menuItemClasses } from "@mui/material/MenuItem";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { stepIconClasses } from "@mui/material/StepIcon";
import { alpha, createTheme } from "@mui/material/styles";

import "./MuiThemeProvider.types";

const { background, text, line, status } = brandColorTokens;

const { palette, breakpoints } = createTheme();

const dialogContentScrollTimeline = "--dialog-content-scroll";

const brandFontFamily = "Aeonik, Arial, sans-serif";

const headingStyle = { color: text.display, fontWeight: 500, fontFeatureSettings: '"ss02" on, "ss08" on' };

export const brandTheme = createTheme({
  palette: {
    primary: palette.augmentColor({
      color: { main: background.actionPrimary, dark: background.actionPrimaryHover, contrastText: text.actionPrimary },
      name: "primary",
    }),
    secondary: palette.augmentColor({
      color: {
        main: background.actionSecondary,
        dark: background.actionSecondaryHover,
        contrastText: text.actionSecondary,
      },
      name: "secondary",
    }),
    tertiary: palette.augmentColor({
      color: {
        main: background.actionTertiary,
        dark: background.actionTertiaryDark,
        contrastText: text.actionTertiary,
      },
      name: "tertiary",
    }),
    error: palette.augmentColor({
      color: { main: status.error, light: status.errorBackground, contrastText: text.bodyInverted },
      name: "error",
    }),
    success: palette.augmentColor({
      color: { main: status.success, light: status.successBackground, contrastText: text.bodyInverted },
      name: "success",
    }),
    warning: palette.augmentColor({
      color: { main: status.warning, light: status.warningBackground, contrastText: text.bodyInverted },
      name: "warning",
    }),
    info: palette.augmentColor({
      color: { main: status.info, light: status.infoBackground, contrastText: text.bodyInverted },
      name: "info",
    }),
    background: { default: background.primary, paper: background.secondary, inverted: background.inverted },
    text: { primary: text.body, secondary: text.bodySubtle },
    divider: line.default,
  },
  shape: { borderRadius: brandRadius.medium },
  typography: {
    fontFamily: brandFontFamily,
    h1: {
      ...headingStyle,
      fontSize: "2rem",
      lineHeight: 1,
      letterSpacing: "-0.03em",
      [breakpoints.up("sm")]: { fontSize: "3rem" },
      [breakpoints.up("md")]: { fontSize: "4rem" },
      [breakpoints.up("lg")]: { fontSize: "4.5rem" },
    },
    h2: {
      ...headingStyle,
      fontSize: "2rem",
      lineHeight: 1,
      letterSpacing: "-0.02em",
      [breakpoints.up("sm")]: { fontSize: "2.5rem" },
      [breakpoints.up("md")]: { fontSize: "3rem" },
    },
    h3: {
      ...headingStyle,
      fontSize: "2rem",
      lineHeight: 0.9,
      letterSpacing: "-0.02em",
      [breakpoints.up("sm")]: { fontSize: "2.5rem" },
      [breakpoints.up("md")]: { fontSize: "3rem" },
    },
    h4: {
      ...headingStyle,
      fontSize: "1.75rem",
      lineHeight: 1,
      letterSpacing: "-0.02em",
      [breakpoints.up("md")]: { fontSize: "2.5rem" },
    },
    h5: {
      ...headingStyle,
      fontSize: "1.5rem",
      lineHeight: 1.1,
      letterSpacing: "-0.01em",
      [breakpoints.up("md")]: { fontSize: "2rem" },
    },
    h6: {
      ...headingStyle,
      fontSize: "1.25rem",
      lineHeight: 1.1,
      letterSpacing: "-0.01em",
      [breakpoints.up("md")]: { fontSize: "1.5rem" },
    },
    subtitle1: { fontSize: "1.125rem", lineHeight: 1.3, fontWeight: 500 },
    body1: { fontSize: "1rem", lineHeight: 1.3 },
    body2: { fontSize: "0.875rem", lineHeight: 1.3 },
    caption: { fontSize: "0.75rem", lineHeight: 1.3 },
    button: { fontSize: "1rem", lineHeight: 1.2, fontWeight: 500, textTransform: "none" },
  },
  components: {
    MuiTextField: { defaultProps: { color: "secondary" } },
    MuiFormControl: { defaultProps: { color: "secondary" } },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          [`&.${menuItemClasses.selected}`]: {
            backgroundColor: alpha(theme.palette.secondary.main, theme.palette.action.selectedOpacity),
            "&:hover": {
              backgroundColor: alpha(
                theme.palette.secondary.main,
                theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity,
              ),
            },
          },
          [`&.${menuItemClasses.selected}.${menuItemClasses.focusVisible}`]: {
            backgroundColor: alpha(
              theme.palette.secondary.main,
              theme.palette.action.selectedOpacity + theme.palette.action.focusOpacity,
            ),
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: background.input,
          [`& .${outlinedInputClasses.notchedOutline}`]: { borderColor: line.input },
        },
      },
    },
    MuiFormHelperText: { styleOverrides: { root: { fontSize: "0.875rem", lineHeight: 1.3 } } },
    MuiCheckbox: { defaultProps: { color: "secondary" } },
    MuiRadio: { defaultProps: { color: "secondary" } },
    MuiSwitch: { defaultProps: { color: "secondary" } },
    MuiDialog: {
      defaultProps: { fullWidth: true, maxWidth: "sm" },
      styleOverrides: {
        paper: {
          borderRadius: brandRadius.large,
          [breakpoints.down("sm")]: { margin: 16, width: "calc(100% - 32px)", maxWidth: "calc(100% - 32px)" },
          "@supports (animation-timeline: scroll())": { timelineScope: dialogContentScrollTimeline },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: "24px 64px 8px 24px",
          "@supports (animation-timeline: scroll())": {
            position: "relative",
            zIndex: 1,
            animation: "dialogTitleScrolledEdge linear both",
            animationTimeline: dialogContentScrollTimeline,
            animationRange: "0 24px",
          },
          "@keyframes dialogTitleScrolledEdge": {
            from: { boxShadow: "none" },
            to: { boxShadow: `0 1px 0 ${line.default}, 0 6px 12px -6px ${alpha(text.body, 0.3)}` },
          },
        },
      },
    },
    MuiDialogContentText: { styleOverrides: { root: { color: text.body } } },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "8px 24px 16px",
          "@supports (animation-timeline: scroll())": { scrollTimeline: `${dialogContentScrollTimeline} block` },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "16px 24px 24px",
          gap: 8,
          "& > :not(style) ~ :not(style)": { marginLeft: 0 },
          [breakpoints.down("sm")]: { flexDirection: "column-reverse", alignItems: "stretch" },
          "@supports (animation-timeline: scroll())": {
            position: "relative",
            zIndex: 1,
            animation: "dialogActionsScrollEdge linear both",
            animationTimeline: dialogContentScrollTimeline,
            animationRange: "calc(100% - 24px) 100%",
          },
          "@keyframes dialogActionsScrollEdge": {
            from: { boxShadow: `0 -1px 0 ${line.default}, 0 -6px 12px -6px ${alpha(text.body, 0.3)}` },
            to: { boxShadow: "none" },
          },
        },
      },
    },
    MuiPaper: { defaultProps: { elevation: 0 } },
    MuiCard: { styleOverrides: { root: { borderRadius: brandRadius.large } } },
    MuiCardContent: { styleOverrides: { root: { padding: 24, "&:last-child": { paddingBottom: 24 } } } },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: "0 24px 24px",
          gap: 8,
          "& > :not(style) ~ :not(style)": { marginLeft: 0 },
          [breakpoints.down("sm")]: { flexDirection: "column-reverse", alignItems: "stretch" },
        },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: brandRadius.medium } },
      variants: [
        { props: { variant: "filled" }, style: { color: text.bodyInverted } },
        {
          props: { variant: "standard", severity: "error" },
          style: {
            backgroundColor: status.errorBackground,
            color: status.error,
            "& .MuiAlert-icon": { color: status.error },
          },
        },
        {
          props: { variant: "standard", severity: "success" },
          style: {
            backgroundColor: status.successBackground,
            color: status.success,
            "& .MuiAlert-icon": { color: status.success },
          },
        },
        {
          props: { variant: "standard", severity: "warning" },
          style: {
            backgroundColor: status.warningBackground,
            color: status.warning,
            "& .MuiAlert-icon": { color: status.warning },
          },
        },
        {
          props: { variant: "standard", severity: "info" },
          style: {
            backgroundColor: status.infoBackground,
            color: status.info,
            "& .MuiAlert-icon": { color: status.info },
          },
        },
      ],
    },
    MuiCircularProgress: { defaultProps: { color: "secondary" } },
    MuiLinearProgress: { defaultProps: { color: "secondary" } },
    MuiLink: {
      defaultProps: { underline: "always", color: "inherit" },
      styleOverrides: { root: { "&:hover": { color: text.linkHover } } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 500, fontSize: "0.875rem" }, sizeSmall: { fontSize: "0.75rem" } },
      variants: [
        {
          props: { color: "default", variant: "filled" },
          style: { backgroundColor: background.tertiary, color: text.body },
        },
        {
          props: { color: "error", variant: "filled" },
          style: { backgroundColor: status.errorBackground, color: status.error },
        },
        {
          props: { color: "success", variant: "filled" },
          style: { backgroundColor: status.successBackground, color: status.success },
        },
        {
          props: { color: "warning", variant: "filled" },
          style: { backgroundColor: status.warningBackground, color: status.warning },
        },
        {
          props: { color: "info", variant: "filled" },
          style: { backgroundColor: status.infoBackground, color: status.info },
        },
      ],
    },
    MuiTabs: { defaultProps: { textColor: "secondary", indicatorColor: "secondary" } },
    MuiTab: { styleOverrides: { root: { fontWeight: 500 } } },
    MuiAccordion: {
      defaultProps: { disableGutters: true },
      styleOverrides: {
        root: {
          borderRadius: brandRadius.large,
          "&::before": { display: "none" },
          "&:first-of-type, &:last-of-type": { borderRadius: brandRadius.large },
          "& + &": { marginTop: 8 },
        },
      },
    },
    MuiAccordionSummary: { styleOverrides: { root: { padding: "8px 24px" } } },
    MuiAccordionDetails: { styleOverrides: { root: { padding: "0 24px 24px" } } },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          [`&.${stepIconClasses.active}, &.${stepIconClasses.completed}`]: { color: background.actionSecondary },
          [`&.${stepIconClasses.active} .${stepIconClasses.text}`]: { fill: text.actionSecondary },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      variants: [
        {
          props: { variant: "outlined", color: "primary" },
          style: {
            borderColor: background.actionPrimary,
            color: text.actionPrimary,
            "&:hover": {
              borderColor: background.actionPrimaryHover,
              backgroundColor: alpha(background.actionPrimary, 0.12),
            },
          },
        },
        { props: { variant: "text", color: "primary" }, style: { color: text.display } },
      ],
      styleOverrides: {
        sizeLarge: { padding: "16px 32px", fontSize: "1rem" },
        sizeMedium: { padding: "12px 24px" },
        sizeSmall: { padding: "8px 16px", fontSize: "0.875rem" },
        containedPrimary: {
          "&.Mui-disabled": { backgroundColor: background.actionPrimary, color: text.actionPrimary, opacity: 0.4 },
        },
        containedSecondary: {
          "&.Mui-disabled": { backgroundColor: background.actionSecondary, color: text.actionSecondary, opacity: 0.4 },
        },
      },
    },
  },
});
